import {
  ADAPTIVE_RT_MIN_STAGE_SLOTS,
  createEmptyStage,
} from '@/stores/adaptiveRtStore';
import { createAdaptiveRtSlotId } from '@/utils/adaptiveRtStageId';
import {
  aliasGroupsToApiPayload,
  normalizeAliasGroupsFromApi,
} from '@/utils/adaptiveRtStructureAliases';
import { normalizeCourseId } from '@/utils/courseId';
import {
  ADAPTIVE_RT_DRAFT_VERSION,
  readAdaptiveRtDraft,
} from '@/utils/adaptiveRtDraftStorage';

/**
 * @typedef {object} AdaptiveRtDraftStageV1
 * @property {string} slotId
 * @property {string|null} planId
 * @property {string} displayName
 * @property {{ total_absorbed_dose: number|null, dose_per_fraction: number|null, number_of_fractions: number|null }} fractionation
 */

/**
 * @typedef {object} AdaptiveRtDraftV1
 * @property {number} version
 * @property {number} courseId
 * @property {string} updatedAt
 * @property {string|null} gatePassedAt
 * @property {string[]} [commonStructures]
 * @property {boolean} [wizardAckExcluded]
 * @property {Array<{ canonical_name: string, members: Array<{ order: number, name: string }> }>} [structure_aliases]
 * @property {object} [wizard_validation] — снимок gate после validate (матрица структур, чеклист)
 * @property {AdaptiveRtDraftStageV1[]} stages
 */

function cloneFractionation(frac) {
  const f = frac || {};
  return {
    total_absorbed_dose: f.total_absorbed_dose ?? null,
    dose_per_fraction: f.dose_per_fraction ?? null,
    number_of_fractions: f.number_of_fractions ?? null,
  };
}

/**
 * @param {import('@/stores/adaptiveRtStore').useAdaptiveRtStore} store
 */
export function storeHasPersistableDraftContent(store) {
  return (store.stages || []).some(
    (s) =>
      !!s?.planId
      || s?.fractionation?.dose_per_fraction != null
      || s?.fractionation?.number_of_fractions != null
  );
}

/**
 * @param {ReturnType<typeof import('@/stores/adaptiveRtStore').useAdaptiveRtStore>} store
 * @param {number|string} courseId
 * @param {{ gatePassed?: boolean }} [opts]
 * @returns {AdaptiveRtDraftV1|null}
 */
export function buildAdaptiveRtDraftFromStore(store, courseId, opts = {}) {
  const cid = normalizeCourseId(courseId);
  if (cid == null || !storeHasPersistableDraftContent(store)) return null;

  const gatePassed = !!opts.gatePassed || !!store.wizardValidation?.can_proceed;
  const existing = readAdaptiveRtDraft(cid);

  let gatePassedAt = null;
  let commonStructures = [];
  if (gatePassed) {
    gatePassedAt = existing?.gatePassedAt || new Date().toISOString();
    commonStructures = (store.commonStructures?.length
      ? store.commonStructures
      : existing?.commonStructures) || [];
    commonStructures = [...commonStructures];
  }

  const draft = {
    version: ADAPTIVE_RT_DRAFT_VERSION,
    courseId: cid,
    updatedAt: new Date().toISOString(),
    gatePassedAt,
    commonStructures,
    wizardAckExcluded: !!store.wizardAckExcluded,
    structure_aliases: aliasGroupsToApiPayload(store.structureAliasGroups),
    stages: (store.stages || []).map((s) => ({
      slotId: s.slotId || createAdaptiveRtSlotId(),
      planId: s.planId || null,
      displayName: s.displayName || '',
      fractionation: cloneFractionation(s.fractionation),
    })),
  };
  if (gatePassed && store.wizardValidation) {
    draft.wizard_validation = store.wizardValidation;
  }
  return draft;
}

/**
 * @param {ReturnType<typeof import('@/stores/adaptiveRtStore').useAdaptiveRtStore>} store
 * @param {AdaptiveRtDraftV1} draft
 */
export function applyAdaptiveRtDraftToStore(store, draft) {
  if (!draft?.stages?.length) return;

  const mapped = draft.stages.map((s) => ({
    slotId: s.slotId || createAdaptiveRtSlotId(),
    planId: s.planId || null,
    displayName: s.displayName || s.planId || '',
    fractionation: cloneFractionation(s.fractionation),
    structureCount: null,
    parameters: [],
    paramsLoaded: false,
    ntcpParameters: [],
    ntcpParamsLoaded: false,
  }));

  while (mapped.length < ADAPTIVE_RT_MIN_STAGE_SLOTS) {
    mapped.push(createEmptyStage());
  }

  store.setStages(mapped);

  if (Array.isArray(draft.structure_aliases) && draft.structure_aliases.length) {
    store.structureAliasGroups = normalizeAliasGroupsFromApi(draft.structure_aliases);
  }

  if (draft.gatePassedAt) {
    const structures = [...(draft.commonStructures || [])];
    store.commonStructures = structures;
    store.wizardAckExcluded = !!draft.wizardAckExcluded;
    if (draft.wizard_validation && typeof draft.wizard_validation === 'object') {
      store.wizardValidation = draft.wizard_validation;
      if (Array.isArray(draft.wizard_validation.common_structures)) {
        store.commonStructures = [...draft.wizard_validation.common_structures];
      }
    } else {
      store.wizardValidation = {
        can_proceed: true,
        common_structures: structures,
        checklist: {
          min_stages: true,
          all_have_dvh: true,
          all_dose_ok: true,
          has_common_structures: structures.length > 0,
          order_defined: true,
        },
        warnings: [],
        stages: [],
        excluded_structures: [],
      };
    }
  } else {
    store.clearWizardValidation();
  }
}

export function formatDraftRestoredMessage(draft) {
  if (!draft?.updatedAt) return 'Восстановлена сохранённая настройка этапов для этого курса.';
  try {
    const d = new Date(draft.updatedAt);
    const when = d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `Восстановлена настройка этапов от ${when}.`;
  } catch {
    return 'Восстановлена сохранённая настройка этапов для этого курса.';
  }
}
